import { useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { GripVertical, CheckCircle2, History, Eye, ExternalLink, ImageOff, Loader2, Bell } from "lucide-react";
import type { Confirmation } from "../types/confirmations.types";
import { usePresignedAttachmentUrl } from "../hooks/usePresignedAttachmentUrl";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { cn } from "@/lib/utils";
import useTranslations from "@/shared/lang/useTranslations";

type ConfirmationCardProps = {
  confirmation: Confirmation;
  hidden?: boolean;
  onCancel?: (id: string) => void;
  onConfirm?: (id: string) => void;
  onViewHistory?: (id: string) => void;
  onView?: (id: string, imageUrl?: string | string[] | null) => void;
  onOpen?: (id: string, imageUrl?: string | string[] | null) => void;
  onNotifyClient?: (id: string) => void;
  onTakerReminder?: (id: string) => void | Promise<void>;
};

const getStatusConfig = (t: (k: keyof import("@/shared/lang/interfaces/confirmations").ConfirmationsTranslations) => string) => ({
  pending: {
    borderClass: "border-l-blue-300 dark:border-l-blue-400",
    badgeClass:
      "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400",
    label: t("card_status_pending"),
  },
  confirmed: {
    borderClass: "border-l-green-300 dark:border-l-green-400",
    badgeClass:
      "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400",
    label: t("card_status_confirmed"),
  },
} as const);

export const ConfirmationCard = ({
  confirmation,
  hidden = false,
  onCancel,
  onConfirm,
  onViewHistory,
  onView,
  onOpen,
  onNotifyClient,
  onTakerReminder,
}: ConfirmationCardProps) => {
  const { t } = useTranslations("confirmations");
  const statusConfig = getStatusConfig(t);
  const config = statusConfig[confirmation.status];
  const isPending = confirmation.status === "pending";
  const [isTakerReminderSending, setIsTakerReminderSending] = useState(false);
  const [attachmentLoadError, setAttachmentLoadError] = useState(false);
  const [failedAnexosIndices, setFailedAnexosIndices] = useState<Set<number>>(new Set());
  const { url: presignedUrl, loading: presignedLoading, error: presignedError } = usePresignedAttachmentUrl(
    confirmation.id,
    confirmation.attachmentUrl
  );
  const anexosUrls = confirmation.attachmentUrls ?? [];
  const hasAnexos = anexosUrls.length > 0;
  const hasAttachmentOrAnexos = Boolean(confirmation.attachmentUrl) || hasAnexos;
  const allImageUrls = [presignedUrl ?? null, ...anexosUrls].filter((u): u is string => Boolean(u));
  const totalImageCount = allImageUrls.length;

  const getImageItemClass = () => {
    if (totalImageCount === 1)
      return "relative shrink-0 w-full max-h-[220px] min-h-[120px] rounded-lg overflow-hidden bg-slate-200/50 dark:bg-zinc-700/50";
    if (totalImageCount === 2)
      return "relative shrink-0 flex-1 min-w-0 max-h-[220px] min-h-[120px] rounded-lg overflow-hidden bg-slate-200/50 dark:bg-zinc-700/50";
    return "relative shrink-0 w-[calc((100%-1rem)/3)] min-w-[calc((100%-1rem)/3)] aspect-4/3 rounded-lg overflow-hidden bg-slate-200/50 dark:bg-zinc-700/50";
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    id: confirmation.id,
    disabled: !isPending,
  });

  const takerReminderButton =
    onTakerReminder ? (
      <button
        type="button"
        onClick={async () => {
          setIsTakerReminderSending(true);
          try {
            await onTakerReminder(confirmation.id);
          } finally {
            setIsTakerReminderSending(false);
          }
        }}
        disabled={isTakerReminderSending}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-sky-800 dark:text-sky-100",
          "bg-sky-100 hover:bg-sky-200/90 dark:bg-sky-900/40 dark:hover:bg-sky-900/60",
          "shadow-sm transition-colors border-0",
          "disabled:opacity-60 disabled:pointer-events-none self-start"
        )}
        aria-label={t("card_button_taker_reminder")}
      >
        <span
          className="inline-flex shrink-0 rounded-full border-2 border-yellow-500 p-1.5 text-yellow-600 dark:border-yellow-400 dark:text-yellow-300"
          aria-hidden
        >
          {isTakerReminderSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Bell className="h-4 w-4" />
          )}
        </span>
        {t("card_button_taker_reminder")}
      </button>
    ) : null;

  const detailsBlock = (
    <>
      {confirmation.note && (
        <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
          <span className="text-[14px] block">
            <span className="text-slate-600 dark:text-slate-300 font-medium">{t("card_label_note")}</span>
            <span className="opacity-75"> {confirmation.note}</span>
          </span>
        </div>
      )}
      {confirmation.createdBy && (
        <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
          <span className="text-[14px]">
            <span className="text-slate-600 dark:text-slate-300 font-medium">{t("card_label_created_by")}</span>
            <span className="opacity-75"> {confirmation.createdBy}</span>
          </span>
        </div>
      )}
      {!isPending && confirmation.updatedByEmail && (
        <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
          <span className="text-[14px]">
            <span className="text-slate-600 dark:text-slate-300 font-medium">{t("card_label_updated_by")}</span>
            <span className="opacity-75"> {confirmation.updatedByEmail}</span>
          </span>
        </div>
      )}
    </>
  );

  const cardContent = (
    <>
      {isPending ? (
        <>
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <span
                className="shrink-0 mt-0.5 text-slate-400 dark:text-slate-500 touch-none"
                aria-hidden
              >
                <GripVertical className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex flex-col gap-2">
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {confirmation.currency}
                </p>
                {takerReminderButton}
              </div>
            </div>
            <span
              className={cn(
                "shrink-0 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-tight",
                config.badgeClass
              )}
            >
              {config.label}
            </span>
          </div>

          <div className="space-y-1.5 text-sm text-slate-500 dark:text-slate-400 mb-6">
            {confirmation.referenceCode && (
              <div className="flex justify-end">
                <span className="text-xs font-semibold text-blue-600/90 dark:text-blue-400/90 tracking-tight">
                  {confirmation.referenceCode}
                </span>
              </div>
            )}
            <div className="flex justify-end text-right text-sm">
              <div>
                <span className="text-slate-500 dark:text-slate-400 font-medium">
                  {t("card_label_created_at")}{" "}
                </span>
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {formatDateTime(confirmation.createdAt ?? confirmation.date)}
                </span>
              </div>
            </div>
            {detailsBlock}
          </div>
        </>
      ) : (
        <>
          <div className="flex gap-3 mb-4">
            <div className="flex flex-col gap-2 min-w-0 flex-1">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {confirmation.currency}
              </p>
              {takerReminderButton}
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0 text-right">
              <span
                className={cn(
                  "shrink-0 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-tight",
                  config.badgeClass
                )}
              >
                {config.label}
              </span>
              {confirmation.referenceCode && (
                <span className="text-xs font-semibold text-blue-600/90 dark:text-blue-400/90 tracking-tight">
                  {confirmation.referenceCode}
                </span>
              )}
              <div className="flex flex-col items-end gap-1 text-right text-sm">
                <div>
                  <span className="text-slate-500 dark:text-slate-400 font-medium">
                    {t("card_label_created_at")}{" "}
                  </span>
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {formatDateTime(confirmation.createdAt ?? confirmation.date)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 font-medium">
                    {t("card_label_updated_at")}{" "}
                  </span>
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {formatDateTime(
                      confirmation.updatedAt ?? confirmation.createdAt ?? confirmation.date
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-1.5 text-sm text-slate-500 dark:text-slate-400 mb-6">
            {detailsBlock}
          </div>
        </>
      )}

      {!isPending && onNotifyClient && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => onNotifyClient(confirmation.id)}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-violet-500 hover:bg-violet-600 active:bg-violet-700 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all"
          >
            <Bell className="h-5 w-5 shrink-0" />
            {t("card_button_notify_client")}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-4">
        {isPending ? (
          <>
            <div className="flex space-x-3">
              {onCancel && (
                <button
                  type="button"
                  onClick={() => onCancel(confirmation.id)}
                  className="text-xs font-semibold text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 transition-colors uppercase tracking-tight"
                >
                  {t("card_button_cancel")}
                </button>
              )}
              {onConfirm && (
                <button
                  type="button"
                  onClick={() => onConfirm(confirmation.id)}
                  className="text-xs font-semibold text-green-500 hover:text-green-600 dark:hover:text-green-400 transition-colors uppercase tracking-tight"
                >
                  {t("card_button_confirm")}
                </button>
              )}
            </div>
            {onViewHistory && (
              <button
                type="button"
                onClick={() => onViewHistory(confirmation.id)}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors uppercase tracking-tight"
              >
                {t("card_button_view_history")}
              </button>
            )}
          </>
        ) : (
          <>
            {confirmation.verifiedByAdmin && (
              <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="text-xs font-semibold uppercase tracking-tight">
                  {t("card_verified")}
                </span>
              </div>
            )}
            {!confirmation.verifiedByAdmin && <div />}
            {onViewHistory && (
              <button
                type="button"
                onClick={() => onViewHistory(confirmation.id)}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors uppercase tracking-tight flex items-center gap-1"
              >
                <History className="h-3.5 w-3.5" />
                {t("card_button_view_history")}
              </button>
            )}
          </>
        )}
      </div>

      {(hasAttachmentOrAnexos || onView || onOpen) && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
          <div className="rounded-lg overflow-hidden bg-slate-100 dark:bg-zinc-800">
            <div className="relative p-2">
              <div
                className={cn(
                  "flex gap-2",
                  totalImageCount > 3
                    ? "overflow-x-auto overflow-y-hidden"
                    : "overflow-hidden"
                )}
              >
                {confirmation.attachmentUrl && (
                  <div className={getImageItemClass()}>
                    {presignedLoading && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
                        <Loader2 className="h-8 w-8 animate-spin shrink-0" />
                        <span className="text-xs">{t("card_loading")}</span>
                      </div>
                    )}
                    {presignedUrl && !presignedLoading && !attachmentLoadError && (
                      <img
                        src={presignedUrl}
                        alt={t("card_alt_comprobante")}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={() => setAttachmentLoadError(true)}
                      />
                    )}
                    {(presignedError || attachmentLoadError) && !presignedLoading && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-slate-500 dark:text-slate-400 p-2">
                        <ImageOff className="h-8 w-8 shrink-0 opacity-60" />
                        <span className="text-[10px] text-center">{t("card_error_load")}</span>
                      </div>
                    )}
                  </div>
                )}
                {!isPending &&
                  anexosUrls.map((url, index) => (
                    <div key={`${url}-${index}`} className={getImageItemClass()}>
                      {!failedAnexosIndices.has(index) ? (
                        <img
                          src={url}
                          alt={t("card_alt_comprobante_n", { n: String(index + 1) })}
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={() => setFailedAnexosIndices((prev) => new Set(prev).add(index))}
                        />
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-slate-500 dark:text-slate-400 p-2">
                          <ImageOff className="h-8 w-8 shrink-0 opacity-60" />
                          <span className="text-[10px] text-center">{t("card_error_load")}</span>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
              {(onView || onOpen) && (allImageUrls.length > 0 || (isPending && confirmation.attachmentUrl)) && (
                <>
                  <div className="absolute inset-0 top-auto h-1/2 bg-gradient-to-t from-black/60 to-transparent pointer-events-none rounded-b-lg" />
                  <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2">
                    {onView && (
                      <button
                        type="button"
                        onClick={() =>
                          onView(
                            confirmation.id,
                            isPending
                              ? presignedUrl
                                ? [presignedUrl]
                                : []
                              : allImageUrls.length > 0
                                ? allImageUrls
                                : presignedUrl
                                  ? [presignedUrl]
                                  : []
                          )
                        }
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/95 dark:bg-zinc-900/95 text-slate-700 dark:text-slate-200 font-medium text-sm hover:bg-white dark:hover:bg-zinc-800 transition-colors shadow-sm"
                      >
                        <Eye className="h-4 w-4 shrink-0" />
                        {t("card_button_view")}
                      </button>
                    )}
                    {(allImageUrls.length === 1 || (isPending && confirmation.attachmentUrl != null)) && onOpen && (
                      <button
                        type="button"
                        onClick={() => onOpen(confirmation.id, isPending && presignedUrl ? [presignedUrl] : allImageUrls)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/95 dark:bg-zinc-900/95 text-slate-700 dark:text-slate-200 font-medium text-sm hover:bg-white dark:hover:bg-zinc-800 transition-colors shadow-sm"
                      >
                        <ExternalLink className="h-4 w-4 shrink-0" />
                        {t("card_button_open")}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (isPending) {
    return (
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        data-confirmation-id={confirmation.id}
        className={cn(
          "bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-5 rounded-xl shadow-sm transition-all duration-200 border-l-4 cursor-grab active:cursor-grabbing",
          config.borderClass,
          hidden && "opacity-0 pointer-events-none transition-opacity duration-200",
          isDragging &&
            !hidden &&
            "opacity-40 scale-[0.98] border-2 border-dashed border-blue-300/50 dark:border-blue-400/50"
        )}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <div
      data-confirmation-id={confirmation.id}
      className={cn(
        "bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-5 rounded-xl shadow-sm border-l-4 transition-opacity duration-200",
        config.borderClass,
        hidden && "opacity-0 pointer-events-none"
      )}
    >
      {cardContent}
    </div>
  );
};
